import type { ConnectionEndpoint } from "./ConnectionEndpointProvider";
import { isVirtualConnection, type AnalyserConnection } from "./VirtualConnections";
import { selectConnectionRoutes } from "./selectConnectionRoutes";
import type { ConnectionRouteRule } from "./connectionRouteRules";
import { endpointAtContour } from "./endpointContour";
type CurvePoint = { x: number; y: number };

// Draws one shadow per pair of displayed dashboard nodes, including reversed
// connections and collapsed ancestors. Each column remains a separate occurrence.
export function connectionCurveShadowKeys(curves: readonly ConnectionCurve[]): Set<string> {
  const pairs = new Set<string>();
  const keys = new Set<string>();
  for (const curve of curves) {
    if (curve.isScrollClipped) continue;
    if (!curve.endpoints.every(endpoint => endpoint.surface === "connection-dashboard")) {
      keys.add(curve.key);
      continue;
    }
    const pair = curve.endpoints.map(endpoint => JSON.stringify([endpoint.providerId,
      endpoint.anchorObjectId ?? endpoint.targetObjectId ?? [endpoint.point.clientX, endpoint.point.clientY]])).sort();
    const key = JSON.stringify(pair);
    if (pairs.has(key)) continue;
    pairs.add(key);
    keys.add(curve.key);
  }
  return keys;
}

export type ConnectionCurve = { key: string; connectionId: string; path: string; ruleId: string; color: string; isLockConnection?: boolean; isScrollClipped?: boolean; endpoints: readonly ConnectionEndpoint[];
  scrollBounds?: { top: number; bottom: number };
  controlPoints: readonly [CurvePoint, CurvePoint, CurvePoint, CurvePoint] };

//
// Counts displayed connections at each dashboard node side, independently of
// their route or the focused node. Collapsed ancestors and outside collection
// points each own their visible totals.
//
export function connectionCurveCounts(curves: readonly ConnectionCurve[]): { key: string; count: number; x: number; y: number; radius: number }[] {
  const groups = new Map<string, { ids: Set<string>; x: number; y: number; side: number; bounds?: { top: number; bottom: number } }>();
  for (const curve of curves) {
    curve.endpoints.forEach((endpoint, index) => {
      if (endpoint.surface !== "connection-dashboard") return;
      const point = curve.controlPoints[index === 0 ? 0 : 3];
      const control = curve.controlPoints[index === 0 ? 1 : 2];
      const side = (endpoint.exitDirection?.x ?? (control.x - point.x)) < 0 ? -1 : 1;
      const anchor = endpoint.anchorObjectId ?? endpoint.targetObjectId ?? `${point.x},${point.y}`;
      const key = endpoint.isScrollClipped
        ? JSON.stringify([endpoint.providerId, "outside", point.x, point.y, side])
        : JSON.stringify([endpoint.providerId, anchor, side]);
      const group = groups.get(key);
      if (group) group.ids.add(curve.connectionId);
      else groups.set(key, { ids: new Set([curve.connectionId]), x: point.x, y: point.y, side,
        bounds: endpoint.isScrollClipped ? undefined : curve.scrollBounds ?? endpoint.scrollGeometry });
    });
  }
  const labels: { key: string; count: number; x: number; y: number; radius: number }[] = [];
  for (const [key, group] of groups) {
    const count = group.ids.size;
    if (count < 2) continue;
    const radius = Math.max(11, String(count).length * 4 + 4);
    if (group.bounds && (group.y - radius < group.bounds.top || group.y + radius > group.bounds.bottom)) continue;
    labels.push({ key, count, radius, x: group.x + group.side * (radius + 8), y: group.y });
  }
  return labels;
}

/** Places the control point outward from its endpoint, with a horizontal fallback for unconstrained anchors. */
function curveControlPoint(endpoint: ConnectionEndpoint, partner: ConnectionEndpoint, length: number, fallbackX: number) {
  const direction = endpoint.exitDirection;
  const magnitude = direction ? Math.hypot(direction.x, direction.y) : 0;
  const valid = Number.isFinite(magnitude) && magnitude > 0;
  const dx = valid && direction ? direction.x / magnitude : fallbackX;
  const dy = valid && direction ? direction.y / magnitude : 0;
  // Keep the editor exit compact so distant targets do not create large loops over its controls.
  let tangentLength = endpoint.surface === "button-editor" ? Math.min(40, Math.max(12, length * 0.35)) : length;
  if (endpoint.surface === "drawing" && !valid) {
    tangentLength = Math.min(tangentLength, Math.abs(partner.point.clientX - endpoint.point.clientX) / 2);
  }
  return { x: endpoint.point.clientX + dx * tangentLength, y: endpoint.point.clientY + dy * tangentLength };
}

/** Draws only the preferred route for each connection as a cubic curve in viewport coordinates. */
export function connectionCurves(
  endpoints: readonly ConnectionEndpoint[],
  connections: readonly AnalyserConnection[],
  rules?: readonly ConnectionRouteRule[],
): ConnectionCurve[] {
  const curves: ConnectionCurve[] = [];
  const colors = new Map<string, string>();
  const lockConnections = new Set<string>();
  for (const connection of connections) {
    colors.set(connection.id, connection.color);
    const isLock = isVirtualConnection(connection)
      ? connection.modelConnections.some(model => model.isLockConnection)
      : connection.isLockConnection;
    if (isLock) lockConnections.add(connection.id);
  }
  for (const route of selectConnectionRoutes(endpoints, connections, rules)) {
    const start = endpointAtContour(route.start, route.end.point);
    const end = endpointAtContour(route.end, route.start.point);
    const x = start.point.clientX,
      y = start.point.clientY;
    const ex = end.point.clientX,
      ey = end.point.clientY;
    const direction = ex >= x ? 1 : -1;
    const distance = start.exitDirection || end.exitDirection
      ? Math.hypot(ex - x, ey - y)
      : Math.abs(ex - x);
    const bend = Math.max(24, distance / 2);
    const first = curveControlPoint(start, end, bend, direction);
    const last = curveControlPoint(end, start, bend, -direction);
    curves.push({
      connectionId: route.connectionId,
      key: route.connectionId,
      ruleId: route.ruleId,
      color: colors.get(route.connectionId)!,
      isLockConnection: lockConnections.has(route.connectionId),
      isScrollClipped: Boolean(route.start.isScrollClipped || route.end.isScrollClipped),
      endpoints: [route.start, route.end],
      scrollBounds: route.start.providerId && route.start.providerId === route.end.providerId && route.start.scrollGeometry && route.end.scrollGeometry
        ? { top: Math.max(route.start.scrollGeometry.clipTop ?? route.start.scrollGeometry.top, route.end.scrollGeometry.clipTop ?? route.end.scrollGeometry.top),
          bottom: Math.min(route.start.scrollGeometry.bottom, route.end.scrollGeometry.bottom) } : undefined,
      controlPoints: [{ x, y }, first, last, { x: ex, y: ey }],
      path: `M ${x} ${y} C ${first.x} ${first.y}, ${last.x} ${last.y}, ${ex} ${ey}`,
    });
  }
  // SVG paints later siblings on top; keep clipped routes behind ordinary connections.
  return curves.sort((a, b) => Number(Boolean(b.isScrollClipped)) - Number(Boolean(a.isScrollClipped)));
}
